import { useQuery } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { api } from "../api/client.js";
import { AsOfBadge, ErrorBanner, InfoTooltip, LoadingSkeleton, pct } from "../components/Common.js";

interface SecurityMetrics {
  asOf: string;
  return1m: number | null;
  returnYtd: number | null;
  return1y: number | null;
  iv30: number | null;
  ivPercentile30: number | null;
  iv90: number | null;
  ivPercentile90: number | null;
  iv180: number | null;
  ivPercentile180: number | null;
  iv365: number | null;
  ivPercentile365: number | null;
  ivPercentileIsSampled: boolean;
  sparkline: number[];
}

interface SecurityRow {
  ticker: string;
  companyName: string;
  gicsSector: string | null;
  isSp500: boolean;
  isNasdaq100: boolean;
  isManuallyAdded: boolean;
  metrics: SecurityMetrics | null;
}

function Sparkline({ values }: { values: number[] }) {
  if (!values || values.length < 2) return <span className="text-slate-600">—</span>;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values
    .map((v, i) => `${(i / (values.length - 1)) * 60},${18 - ((v - min) / range) * 18}`)
    .join(" ");
  const up = values[values.length - 1]! >= values[0]!;
  return (
    <svg width="60" height="18" className="inline-block">
      <polyline fill="none" stroke={up ? "#34d399" : "#f87171"} strokeWidth="1.5" points={points} />
    </svg>
  );
}

const columnHelper = createColumnHelper<SecurityRow>();

export function UniversePage() {
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState<string>("");
  const [indexFilter, setIndexFilter] = useState<string>("");
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["universe"],
    queryFn: () => api.get<{ securities: SecurityRow[]; asOf: string }>("/universe"),
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("ticker", { header: "Ticker" }),
      columnHelper.accessor("companyName", { header: "Company" }),
      columnHelper.accessor((r) => (r.isSp500 ? "S&P500 " : "") + (r.isNasdaq100 ? "NDX100" : ""), {
        id: "index",
        header: "Index",
      }),
      columnHelper.accessor("gicsSector", { header: "Sector" }),
      columnHelper.accessor((r) => r.metrics?.return1m ?? null, {
        id: "return1m",
        header: "1M Return",
        cell: (c) => pct(c.getValue()),
      }),
      columnHelper.accessor((r) => r.metrics?.returnYtd ?? null, {
        id: "returnYtd",
        header: "YTD Return",
        cell: (c) => pct(c.getValue()),
      }),
      columnHelper.accessor((r) => r.metrics?.return1y ?? null, {
        id: "return1y",
        header: "1Y Return",
        cell: (c) => pct(c.getValue()),
      }),
      columnHelper.accessor((r) => r.metrics?.iv30 ?? null, {
        id: "iv30",
        header: () => (
          <span>
            30D IV<InfoTooltip text="Standardized 30-day ATM-forward implied volatility from the calibrated SVI surface." />
          </span>
        ),
        cell: (c) => pct(c.getValue()),
      }),
      columnHelper.accessor((r) => r.metrics?.ivPercentile30 ?? null, {
        id: "ivp30",
        header: () => (
          <span>
            30D IVP<InfoTooltip text="Percentile rank of today's 30D IV vs. its trailing ~1Y history. Sampled backfills are flagged." />
          </span>
        ),
        cell: (c) => {
          const v = c.getValue();
          const sampled = c.row.original.metrics?.ivPercentileIsSampled;
          return (
            <span className={sampled ? "text-amber-400" : ""}>
              {v === null ? "—" : `${v.toFixed(0)}%`}
              {sampled && v !== null && "*"}
            </span>
          );
        },
      }),
      columnHelper.accessor((r) => r.metrics?.iv365 ?? null, {
        id: "iv365",
        header: "12M IV",
        cell: (c) => pct(c.getValue()),
      }),
      columnHelper.display({
        id: "sparkline",
        header: "Trend",
        cell: (c) => <Sparkline values={c.row.original.metrics?.sparkline ?? []} />,
      }),
    ],
    [],
  );

  const filtered = useMemo(() => {
    let rows = data?.securities ?? [];
    if (sectorFilter) rows = rows.filter((r) => r.gicsSector === sectorFilter);
    if (indexFilter === "SP500") rows = rows.filter((r) => r.isSp500);
    if (indexFilter === "NASDAQ100") rows = rows.filter((r) => r.isNasdaq100);
    return rows;
  }, [data, sectorFilter, indexFilter]);

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, globalFilter: search },
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    globalFilterFn: (row, _col, value) => {
      const q = String(value).toLowerCase();
      return row.original.ticker.toLowerCase().includes(q) || row.original.companyName.toLowerCase().includes(q);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const sectors = useMemo(
    () => Array.from(new Set((data?.securities ?? []).map((r) => r.gicsSector).filter(Boolean))) as string[],
    [data],
  );

  function exportCsv() {
    const rows = table.getFilteredRowModel().rows;
    const header = ["ticker", "company", "sector", "1m_return", "ytd_return", "1y_return", "iv30", "ivp30", "iv365"];
    const lines = rows.map((r) => {
      const s = r.original;
      return [
        s.ticker,
        s.companyName,
        s.gicsSector ?? "",
        s.metrics?.return1m ?? "",
        s.metrics?.returnYtd ?? "",
        s.metrics?.return1y ?? "",
        s.metrics?.iv30 ?? "",
        s.metrics?.ivPercentile30 ?? "",
        s.metrics?.iv365 ?? "",
      ].join(",");
    });
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "universe-screen.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="field-input max-w-xs"
          placeholder="Search ticker / company"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="field-input max-w-[10rem]" value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)}>
          <option value="">All sectors</option>
          {sectors.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select className="field-input max-w-[10rem]" value={indexFilter} onChange={(e) => setIndexFilter(e.target.value)}>
          <option value="">All indices</option>
          <option value="SP500">S&P 500</option>
          <option value="NASDAQ100">Nasdaq-100</option>
        </select>
        <div className="ml-auto flex items-center gap-2">
          {data && <AsOfBadge asOf={data.asOf} />}
          <button className="btn" onClick={exportCsv}>
            Export CSV
          </button>
        </div>
      </div>

      {isLoading && <LoadingSkeleton rows={10} />}
      {error && <ErrorBanner message={(error as Error).message} />}

      {!isLoading && !error && (
        <div className="panel overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-surface-border text-xs uppercase text-slate-400">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      className="cursor-pointer select-none px-3 py-2 hover:text-slate-200"
                      onClick={h.column.getToggleSortingHandler()}
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {{ asc: " ▲", desc: " ▼" }[h.column.getIsSorted() as string] ?? ""}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-surface-border/60 hover:bg-slate-800/30">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-1.5 font-mono text-xs">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-surface-border px-3 py-2 text-xs text-slate-500">
            {table.getRowModel().rows.length} of {data?.securities.length ?? 0} securities · * = IV percentile
            based on incomplete/sampled history
          </div>
        </div>
      )}
    </div>
  );
}
